import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { companyInfo } from '../mockData';

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(process.env.REACT_APP_FORMSPREE_ENDPOINT, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        projectType: formData.projectType,
        message: formData.message,
        _subject: `New Quote Request from ${formData.name}`,
      });

      if (response.status === 200) {
        toast.success('Quote request submitted successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit quote request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote-section" className="quote-section">
      <div className="container">
        <div className="quote-wrapper">
          <div className="quote-info">
            <div className="section-header text-left">
              <span className="section-badge">Get In Touch</span>
              <h2 className="section-title">Request A Quote</h2>
              <p className="section-description">
                Share your project details and our team will provide you with 
                a customized quote
              </p>
            </div>

            <div className="contact-info-cards">
              <div className="contact-card">
                <div className="contact-icon">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="contact-label">Phone</h4>
                  <a href={`tel:${companyInfo.phone}`} className="contact-value">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-icon">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="contact-label">Email</h4>
                  <a href={`mailto:${companyInfo.email}`} className="contact-value">
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-icon">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="contact-label">Location</h4>
                  <p className="contact-value">{companyInfo.address}</p>
                </div>
              </div>

              <a 
                href="https://wa.me/97128779072"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-card whatsapp-card"
                data-testid="whatsapp-contact-card"
              >
                <div className="contact-icon whatsapp-icon-bg">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h4 className="contact-label">WhatsApp</h4>
                  <span className="contact-value">(02) 878 1762</span>
                </div>
              </a>

              <a 
                href={companyInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-card instagram-card"
                data-testid="instagram-contact-card"
              >
                <div className="contact-icon instagram-icon-bg">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div>
                  <h4 className="contact-label">Instagram</h4>
                  <span className="contact-value">@greenartlandscape.uae</span>
                </div>
              </a>
            </div>

            <div className="map-container">
              <iframe
                title="Green Art Location"
                data-testid="google-map-embed"
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&q=Riqat+Halwan+St,+Abu+Dhabi,+UAE`}
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="quote-form-container">
            <form onSubmit={handleSubmit} className="quote-form" data-testid="quote-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Full Name *</label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number *</label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+971 XX XXX XXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="projectType" className="form-label">Project Type</label>
                <Input
                  id="projectType"
                  name="projectType"
                  type="text"
                  placeholder="e.g., Residential Landscaping, Pool Installation"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Project Details *</label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your project requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="form-textarea"
                  rows={5}
                />
              </div>

              <Button 
                type="submit" 
                className="btn-primary btn-3d w-full"
                size="lg"
                disabled={isSubmitting}
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Quote Request
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
